/**
 * PR 6b — Phase 1 — Typed API error envelope.
 *
 * Bridges raw `AxiosError` instances from OMS calls into a discriminated
 * `ApiError` union the UI can switch on. Centralised here so every service
 * (`driftApi.ts`, future ones) maps errors the same way and UI components
 * don't need to spelunk into `error.response.data.error` strings.
 *
 * Backend reference: OMS Go handlers in
 * `microservices/oms-microservice/internal/handler/admin_proposal.go` always
 * respond with `{ "error": "<code>", "detail"?: "<human>" }` (see
 * `respondJSON` patterns at e.g. lines 134, 491, 537, 542). Status code
 * partitioning:
 *
 *   400  — validation / malformed body
 *   401  — token missing/expired (auth interceptor in `api.tsx` handles re-login)
 *   403  — forbidden (RBAC, e.g. comercial requesting admin field)
 *   404  — order/revision not found
 *   406  — `Accept-Version` mismatch
 *   409  — domain conflict (`invalid_status_transition`, `margin_at_risk_blocked`,
 *          `reference_type_forbidden_for_role` lives as 403 — keep both)
 *   422  — semantic validation (e.g. justification too short)
 *   429  — rate-limited
 *   5xx  — server / network
 *
 * Network-level failures (no response object: timeout, DNS, offline) map to
 * `{ kind: "network" }`.
 *
 * Convention: callers use `if (err.kind === "..." )` not instanceof. The
 * raw AxiosError is preserved as `.raw` for debugging / Sentry reporting,
 * but UI MUST NOT branch on it.
 */

import { AxiosError } from "axios";

export type ApiErrorCode =
  // 4xx — client
  | "validation"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "version_mismatch"
  | "conflict"
  | "unprocessable"
  | "rate_limited"
  // 5xx / network
  | "server"
  | "network";

export interface ApiError {
  kind: ApiErrorCode;
  /** Backend `error` field (machine code) when present. */
  code?: string;
  /** Backend `detail` field (human-readable) when present. */
  detail?: string;
  /** HTTP status when a response was received. Undefined for `network`. */
  status?: number;
  /** Original error preserved for logging/Sentry; NEVER branch on this. */
  raw: AxiosError;
}

interface BackendErrorBody {
  error?: string;
  detail?: string;
  // Allow forward-compat fields without erroring tsc.
  [k: string]: unknown;
}

/**
 * Map an `AxiosError` into our typed `ApiError`. Pure function — does NOT
 * throw, does NOT log, does NOT call out to any I/O. Safe to call from
 * anywhere including error boundaries.
 *
 * Status-to-kind partitioning is exhaustive: any unknown status defaults to
 * `server` so the UI surfaces a generic "tente novamente" rather than
 * silently swallowing the error.
 */
export function toApiError(err: unknown): ApiError {
  // Non-AxiosError shouldn't reach here in practice (services always axios),
  // but be defensive: wrap as network so the UI shows a generic message.
  if (!(err instanceof AxiosError)) {
    return {
      kind: "network",
      detail: err instanceof Error ? err.message : "unknown error",
      raw: new AxiosError(err instanceof Error ? err.message : "unknown error"),
    };
  }

  // No response → DNS, timeout, offline, CORS-pre-flight failure.
  if (!err.response) {
    return {
      kind: "network",
      detail: err.message,
      raw: err,
    };
  }

  const status = err.response.status;
  const body = (err.response.data ?? {}) as BackendErrorBody;
  const code = typeof body.error === "string" ? body.error : undefined;
  const detail = typeof body.detail === "string" ? body.detail : undefined;

  let kind: ApiErrorCode;
  switch (status) {
    case 400:
      kind = "validation";
      break;
    case 401:
      kind = "unauthorized";
      break;
    case 403:
      kind = "forbidden";
      break;
    case 404:
      kind = "not_found";
      break;
    case 406:
      kind = "version_mismatch";
      break;
    case 409:
      kind = "conflict";
      break;
    case 422:
      kind = "unprocessable";
      break;
    case 429:
      kind = "rate_limited";
      break;
    default:
      kind = status >= 500 ? "server" : "server";
      break;
  }

  return { kind, code, detail, status, raw: err };
}

/** UI-friendly default message for an `ApiError`. UI may override per kind
 *  or per code; this exists so simple alert sites have a one-liner. */
export function defaultMessageFor(err: ApiError): string {
  if (err.detail) return err.detail;
  switch (err.kind) {
    case "validation":
      return "Pedido inválido.";
    case "unauthorized":
      return "Sessão expirada. Refaz o login.";
    case "forbidden":
      return "Sem permissão para esta acção.";
    case "not_found":
      return "Não encontrado.";
    case "version_mismatch":
      return "Versão da API incompatível. Recarrega a página.";
    case "conflict":
      return "Conflito de estado. Refresca e tenta de novo.";
    case "unprocessable":
      return "Dados insuficientes ou inválidos.";
    case "rate_limited":
      return "Demasiados pedidos. Aguarda alguns segundos.";
    case "server":
      return "Erro do servidor. Tenta de novo.";
    case "network":
      return "Sem ligação. Verifica a rede.";
  }
}

/**
 * Extract a human-readable error string from an unknown thrown value.
 * Checks for AxiosError shape first, falls back to generic message.
 * Use this in catch blocks when all you need is a string for the UI:
 *
 *   try { ... } catch (e) {
 *     setError(extractApiError(e, "Erro ao guardar."));
 *   }
 */
export function extractApiError(e: unknown, fallback: string): string {
  if (typeof e === "object" && e !== null) {
    // AxiosError shape: response.data.error
    if ("response" in e) {
      const resp = (e as { response?: { data?: { error?: string } } }).response;
      if (resp?.data?.error) return resp.data.error;
    }
    // Standard Error
    if (e instanceof Error) return e.message;
  }
  return fallback;
}
